import { useFilePicker } from 'use-file-picker';
import Button from '@ui/Button';
import React, { useRef } from 'react';
import * as yup from 'yup';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import Typography from '@ui/Typography';
import TextArea from '@ui/TextArea';
import { FaUser } from 'react-icons/fa';
import NonProfitOnBoardFormEditAvatarStep from './NonProfitOnBoardFormEditAvatarStep';
import { defaults, pick } from 'lodash-es';

const schema = yup.object().shape({
  message: yup
    .string()
    .required('Please provide a brief message about yourself')
    .min(10, 'Message must be at least 10 characters')
    .max(1000, 'Message must be at most 1000 characters'),
});

function NonProfitOnBoardFormAboutStep(props) {
  const { onSubmit, onBack, formData } = props;

  const [showImageEditor, setShowImageEditor] = React.useState(false);
  const [avatarImage, setAvatarImage] = React.useState(
    formData ? formData.avatar : null
  );

  const [openFileSelector, { filesContent, loading, errors: fileErrors }] =
    useFilePicker({
      readAs: 'DataURL',
      accept: 'image/*',
      multiple: false,
      maxFileSize: 5, // in megabytes
    });

  const defaultValues = defaults(pick(formData, ['message']), {
    message: '',
  });

  const form = useForm({
    defaultValues: defaultValues,
    mode: 'onChange',
    resolver: yupResolver(schema),
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
  } = form;

  React.useEffect(() => {
    if (filesContent && filesContent[0]) {
      setShowImageEditor(true);
    }
  }, [filesContent]);

  const handleFormSubmit = (data) => {
    onSubmit({
      ...data,
      avatar: avatarImage,
    });
  };

  const handleImageSubmit = (file) => {
    if (file) {
      setAvatarImage(
        Object.assign(file, {
          preview: URL.createObjectURL(file),
        })
      );
      setShowImageEditor(false);
    }
  };

  const showFileSelector = () => {
    openFileSelector();
  };

  if (showImageEditor) {
    return (
      <NonProfitOnBoardFormEditAvatarStep
        image={filesContent[0].content}
        onSubmit={handleImageSubmit}
      />
    );
  }

  return (
    <div className="flex flex-col gap-12 flex-1 w-full">
      <Typography variant="dialogtitle">Set Up Your Account</Typography>

      <form
        onSubmit={handleSubmit(handleFormSubmit)}
        className="flex flex-col gap-6">
        <div className="flex items-center gap-4">
          <div className="w-20 h-20 rounded-full">
            {avatarImage ? (
              <img
                src={avatarImage.preview}
                alt="avatar"
                className="w-full h-full rounded-full"
              />
            ) : (
              <div className="bg-primary-light-1 p-5 w-full h-full">
                <FaUser className="text-primary w-full h-full" />
              </div>
            )}
          </div>
          <Button
            variant="outlined"
            color="primary"
            onClick={showFileSelector}
            type="button">
            Upload Picture
          </Button>
        </div>

        <Controller
          name="message"
          control={control}
          render={({ field: { ref, ...rest } }) => (
            <TextArea
              label="Tell us more about yourself"
              required
              {...rest}
              error={errors.message?.message}
            />
          )}
        />

        <div className="flex justify-between flex-rows gap-4">
          <div className="flex-flow-col">
            <Button
              variant="outlined"
              color="primary"
              className="mt-2"
              onClick={onBack}>
              Back
            </Button>
          </div>

          <div className="flex-flow-col">
            <Button
              variant="filled"
              color="primary"
              type="submit"
              className="mt-2">
              Next
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}

export default NonProfitOnBoardFormAboutStep;
